import React, { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { TicketKey } from 'flow/types/EntityKeys';

import {
    selectApiHasMoreTicketsAllStatesData,
    selectTicketKeysAllStatesSorted,
    selectTicketsSortDirection,
    selectTicketsSortField,
} from 'flow/selectors/TicketsSelectors';

import {
    fetchTicketsAllLatest,
    fetchTicketsAllNext,
} from 'flow/actions/tickets-action';

import EmptyBubble from 'flow/components/nav/empty-states/EmptyBubble';
import TicketsList from 'flow/components/tickets/tickets-list/TicketsList';

interface Props {
    itemSelectedKey?: TicketKey;
    scrollableTargetId: string;
    listItemClicked: (key: TicketKey) => void;
}

const TicketsListAllWrapper = ({
    itemSelectedKey,
    scrollableTargetId,
    listItemClicked,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const [isFetchingLatestData, setIsFetchingLatestData] = useState(false);

    const apiHasMoreData = useAppSelector(selectApiHasMoreTicketsAllStatesData);
    const keys = useAppSelector(selectTicketKeysAllStatesSorted);

    const sortField = useAppSelector(selectTicketsSortField);
    const sortDirection = useAppSelector(selectTicketsSortDirection);

    useEffect(() => {
        setIsFetchingLatestData(true);
        dispatch(fetchTicketsAllLatest).finally(() => {
            setIsFetchingLatestData(false);
        });
    }, [dispatch, setIsFetchingLatestData, sortField, sortDirection]);

    const fetchNext = useCallback(
        () => dispatch(fetchTicketsAllNext),
        [dispatch],
    );

    return keys.isEmpty() && !isFetchingLatestData ? (
        <EmptyBubble
            title={i18next.t('tickets.no_tickets')}
            imageId={'supervisorCircle'}
        />
    ) : (
        <TicketsList
            key={'TICKETS_ALL_WRAPPER'}
            itemKeys={keys}
            handleListItemClicked={listItemClicked}
            itemSelectedKey={itemSelectedKey}
            displayLoadingItem={isFetchingLatestData && keys.isEmpty()}
            onReachedEndOfList={fetchNext}
            canFetchMoreData={apiHasMoreData}
            scrollableTargetId={scrollableTargetId}
        />
    );
};

export default React.memo(TicketsListAllWrapper);
